import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";
import { formatToCustomLocale } from "../../../utilities";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";

// Constants
const RENDER_PIPELINE_ALPHA = 0.001; // Keep in pipeline without visible render
const VISIBLE_ALPHA = 1;

// Coin collect spine positioning configuration
export const COIN_COLLECT_CONFIG = {
  verticalOffsetMultiplier: 0.4, // Y offset as multiplier of symbolHeight (positive = down, negative = up)
  horizontalOffsetMultiplier: 0, // X offset as multiplier of symbolWidth (positive = right, negative = left)
  scaleMultiplier: 1.0, // Scale multiplier (1.0 = no change)
};

/**
 * HoldAndWinCoinCollectController - Manages coin collection particle animations
 * Displays particle effects traveling from coin positions to the collector
 */
class HoldAndWinCoinCollectController {
  constructor(
    container,
    dimensions,
    symbolWidth,
    symbolHeight,
    device,
    orientation
  ) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.device = device;
    this.orientation = orientation;

    // Multiple spine instances - one per position (5 reels × 4 symbols = 20 spines)
    this.coinCollectSpines = {}; // { "reelIndex-symbolIndex": { spine, valueTextContainer, valueText, boneUpdateTicker } }
    this.spineContainer = new PIXI.Container(); // Container for all spine instances
    this.activeTickers = new Set(); // Track all active tickers for cleanup

    this.progressMultiplierSpine = null; // Progress multiplier transfer animation
    this.initialized = false;
    this.isCleanedUp = false;

    // Add spine container to main container
    this.container.addChild(this.spineContainer);

    this.initializeCoinCollectSpines();
  }

  /**
   * Check if controller is ready for operations
   */
  isReady() {
    return (
      Object.keys(this.coinCollectSpines).length > 0 &&
      this.initialized &&
      !this.isCleanedUp
    );
  }

  /**
   * Apply proportional scaling and centering to all spine animations
   */
  applyScaleAndCenter() {
    if (!this.dimensions) return;

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let baseScale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Apply device/orientation-specific multipliers if needed
    const deviceMultiplier = this.getDeviceScaleMultiplier();
    baseScale *= deviceMultiplier;

    // Apply scale multiplier from config
    baseScale *= COIN_COLLECT_CONFIG.scaleMultiplier;

    // Apply scale to the spine container (not individual spines)
    // The container will scale all coin collect spines AND progress multiplier inside it
    if (this.spineContainer) {
      this.spineContainer.scale.set(baseScale);
    }

    // Calculate dynamic offsets based on symbol size
    const horizontalOffset =
      this.symbolWidth * COIN_COLLECT_CONFIG.horizontalOffsetMultiplier;
    const verticalOffset =
      this.symbolHeight * COIN_COLLECT_CONFIG.verticalOffsetMultiplier;

    // Position the spine container (all children inherit this position)
    if (this.spineContainer) {
      this.spineContainer.x = horizontalOffset;
      this.spineContainer.y = verticalOffset;
    }
  }

  /**
   * Get device-specific scale multiplier
   * Currently returns 1.0 for all devices (can be adjusted if needed)
   */
  getDeviceScaleMultiplier() {
    return 1.0;
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   */
  updateDimensions(dimensions, symbolWidth, symbolHeight, device, orientation) {
    const oldDimensions = this.dimensions;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== dimensions?.width ||
      oldDimensions.height !== dimensions?.height ||
      this.device !== device ||
      this.orientation !== orientation;

    const symbolSizeChanged =
      this.symbolWidth !== symbolWidth || this.symbolHeight !== symbolHeight;

    // Only proceed if something actually changed
    if (!dimensionsChanged && !symbolSizeChanged) {
      return;
    }

    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.device = device;
    this.orientation = orientation;

    if (this.symbolHeight && this.symbolWidth) {
      this.applyScaleAndCenter();
    }
  }

  /**
   * Initialize multiple coin collect spines - one per position (5 reels × 4 symbols = 20)
   */
  initializeCoinCollectSpines() {
    try {
      // Create 20 spine instances (5 reels × 4 symbols)
      for (let reelIndex = 0; reelIndex < 5; reelIndex++) {
        for (let symbolIndex = 0; symbolIndex < 4; symbolIndex++) {
          const spine = this.createCoinCollectSpine();

          if (!spine || !spine.skeleton || !spine.state) {
            continue;
          }

          // Add to spine container
          this.spineContainer.addChild(spine);
          spine.zIndex = 1;
          spine.alpha = RENDER_PIPELINE_ALPHA; // Hidden initially

          // Warm up the spine
          const animationName = `${reelIndex + 1}_${symbolIndex + 1}`;
          const animations = spine.skeleton?.data?.animations?.map(a => a.name);
          if (animations && animations.includes(animationName)) {
            spine.state.setAnimation(0, animationName, false);
            spine.state.update(0.016);
            try {
              if (spine.skeleton.physics !== undefined) {
                spine.skeleton.updateWorldTransform();
              }
            } catch (transformError) {
              // Skip
            }
            spine.state.clearTracks();
          }
          spine.alpha = RENDER_PIPELINE_ALPHA;

          // Create value text for this spine
          const valueTextContainer = new PIXI.Container();
          valueTextContainer.name = `coin_value_text_${reelIndex}_${symbolIndex}`;
          valueTextContainer.alpha = 0;
          valueTextContainer.zIndex = 1000;
          spine.addChild(valueTextContainer);

          const valueText = new PIXI.BitmapText("0", {
            fontName: "font",
            fontSize: 40,
          });
          valueText.anchor.set(0.5);
          valueTextContainer.addChild(valueText);

          // Store spine data
          const key = `${reelIndex}-${symbolIndex}`;
          this.coinCollectSpines[key] = {
            spine,
            valueTextContainer,
            valueText,
            boneUpdateTicker: null,
            animationName
          };
        }
      }

      this.applyScaleAndCenter();
      this.initializeProgressMultiplierSpine();

      this.initialized = true;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   */
  initializeSpineStructure() {
    if (!this.coinCollectSpine || !this.coinCollectSpine.state) {
      return;
    }

    try {
      const animations = this.coinCollectSpine.skeleton?.data?.animations?.map(
        (anim) => anim.name
      );
      if (animations && animations.length > 0) {
        // Use first animation (1_1) to warm up the spine
        this.coinCollectSpine.state.setAnimation(0, animations[0], false);
        this.coinCollectSpine.state.update(0.016);

        try {
          if (this.coinCollectSpine.skeleton.physics !== undefined) {
            this.coinCollectSpine.skeleton.updateWorldTransform();
          }
        } catch (transformError) {
          // Skip updateWorldTransform if physics is not initialized
        }

        this.coinCollectSpine.state.clearTracks();
      }

      // Keep spine in render pipeline
      this.coinCollectSpine.alpha = RENDER_PIPELINE_ALPHA;
    } catch (error) {
      // Silent fail - warmup is optional
    }
  }

  /**
   * Create coin collect spine from loaded assets
   */
  createCoinCollectSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "hns_coin_collect-skeleton";
      const atlasAlias = "hns_coin_collect-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const coinCollectSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (
        !coinCollectSpine ||
        !coinCollectSpine.skeleton ||
        !coinCollectSpine.state
      ) {
        return null;
      }

      if (
        !coinCollectSpine.skeleton.data ||
        !coinCollectSpine.skeleton.data.animations
      ) {
        return null;
      }

      return coinCollectSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Create progress multiplier spine from loaded assets
   */
  createProgressMultiplierSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "hns_progress_multiplier-skeleton";
      const atlasAlias = "hns_progress_multiplier-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const progressMultiplierSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (
        !progressMultiplierSpine ||
        !progressMultiplierSpine.skeleton ||
        !progressMultiplierSpine.state
      ) {
        return null;
      }

      return progressMultiplierSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize progress multiplier animation spine
   */
  initializeProgressMultiplierSpine() {
    try {
      this.progressMultiplierSpine = this.createProgressMultiplierSpine();
      if (!this.progressMultiplierSpine) {
        return;
      }

      // Add to spineContainer (not main container) so it inherits global scaling
      this.spineContainer.addChild(this.progressMultiplierSpine);
      this.progressMultiplierSpine.zIndex = 3; // Above coin collect spines
      this.progressMultiplierSpine.alpha = RENDER_PIPELINE_ALPHA; // Hidden initially

      // Position at center (0,0) - container handles global scaling and positioning
      this.progressMultiplierSpine.x = 0;
      this.progressMultiplierSpine.y = 0;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Initialize the value text container (cached, ready to use)
   * Text will be repositioned to different bones per animation
   */
  initializeValueText() {
    if (!this.coinCollectSpine) return;

    try {
      // Create text container
      this.valueTextContainer = new PIXI.Container();
      this.valueTextContainer.name = "value_text_container";
      this.valueTextContainer.alpha = 0; // Hidden by default
      this.coinCollectSpine.addChild(this.valueTextContainer);

      // PRE-CREATE the bitmap text (cached, ready to use)
      // Calculate font size to match coin symbol text size
      const baseFontSize = Settings.SYMBOL_HEIGHT * 0.25;
      const fontSize = Math.floor(
        baseFontSize * (this.symbolHeight / Settings.SYMBOL_HEIGHT)
      );

      this.valueText = new PIXI.BitmapText("0", {
        fontName: "font",
        fontSize: fontSize,
      });

      this.valueText.anchor.set(0.5);
      this.valueTextContainer.addChild(this.valueText);

      // Counter-scale the text to compensate for spine's scale
      // The spine is scaled down to fit the reelset, so we scale the text up
      // Apply extra multiplier to match coin symbol text size
      const spineScale = this.coinCollectSpine.scale.x;
      if (spineScale > 0) {
        const counterScale = (1 / spineScale) * 1; // 0.5x multiplier for proper size
        this.valueText.scale.set(counterScale);
      }
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Update the cached value text (no creation, just update)
   * @param {number} value - The coin display value (already multiplied by bet)
   */
  updateValueText(value) {
    if (!this.valueText) return;

    try {
      // Format the value with locale formatting (e.g., "1,234.50")
      const formattedValue = formatToCustomLocale(value);
      this.valueText.text = formattedValue;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Show the value text
   */
  showValueText() {
    if (this.valueTextContainer) {
      this.valueTextContainer.alpha = 1;
    }
  }

  /**
   * Hide the value text
   */
  hideValueText() {
    if (this.valueTextContainer) {
      this.valueTextContainer.alpha = 0;
    }
  }

  /**
   * Update text position from a specific bone's world transform
   * @param {string} boneName - The bone name to track (e.g., "1_1", "2_3")
   */
  updateTextFromBone(boneName) {
    if (!this.coinCollectSpine || !this.valueTextContainer) return;

    try {
      const bone = this.coinCollectSpine.skeleton.findBone(boneName);
      if (!bone) return;

      // Get world transform from bone
      const worldX = bone.worldX;
      const worldY = bone.worldY;

      // Apply position to container
      this.valueTextContainer.x = worldX;
      this.valueTextContainer.y = worldY;

      // Keep text horizontal (no rotation)
      // Don't apply bone rotation - text should always be readable
      this.valueTextContainer.rotation = 0;

      // Apply scale (but keep it uniform to avoid distortion)
      const scaleX = bone.getWorldScaleX();
      const scaleY = bone.getWorldScaleY();
      this.valueTextContainer.scale.set(scaleX, scaleY);
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Start updating text position every frame to follow bone animation
   * @param {string} boneName - The bone name to track
   */
  startBoneUpdateLoop(boneName) {
    if (this.boneUpdateTicker) {
      this.stopBoneUpdateLoop();
    }

    this.activeBoneName = boneName;
    // Use shared ticker instead of creating new instance
    this.boneUpdateTicker = () => {
      // Defensive check: stop if cleaned up
      if (this.isCleanedUp || !this.valueTextContainer) return;
      this.updateTextFromBone(this.activeBoneName);
    };
    PIXI.Ticker.shared.add(this.boneUpdateTicker);
  }

  /**
   * Stop updating text position
   */
  stopBoneUpdateLoop() {
    if (this.boneUpdateTicker) {
      PIXI.Ticker.shared.remove(this.boneUpdateTicker);
      this.boneUpdateTicker = null;
    }
  }

  /**
   * Play collection animation for a specific coin position
   * @param {number} reelIndex - Reel index (0-based)
   * @param {number} symbolIndex - Symbol index (0-based)
   * @param {number} coinValue - The coin value to display during animation
   * @returns {Promise} - Resolves when animation completes
   */
  playCollectionAnimation(reelIndex, symbolIndex, coinValue = 0) {
    return new Promise((resolve) => {
      if (!this.isReady()) {
        resolve();
        return;
      }

      // Get the specific spine for this position
      const key = `${reelIndex}-${symbolIndex}`;
      const spineData = this.coinCollectSpines[key];

      if (!spineData || !spineData.spine || !spineData.spine.state) {
        resolve();
        return;
      }

      const { spine, valueTextContainer, valueText, animationName } = spineData;

      try {
        // Update and show the value text for this specific spine
        if (coinValue > 0 && valueText) {
          const formattedValue = formatToCustomLocale(Math.floor(coinValue));
          valueText.text = formattedValue;

          // Position text at the animation's bone
          this.updateTextFromBoneForSpine(spine, animationName, valueTextContainer);

          // Show value text
          valueTextContainer.alpha = 1;

          // Start tracking this specific bone during animation
          const ticker = () => {
            if (this.isCleanedUp || !valueTextContainer) return;
            this.updateTextFromBoneForSpine(spine, animationName, valueTextContainer);
          };
          spineData.boneUpdateTicker = ticker;
          this.activeTickers.add(ticker);
          PIXI.Ticker.shared.add(ticker);
        }

        // Make spine visible
        spine.alpha = VISIBLE_ALPHA;

        // Clear any previous animations and listeners for THIS spine
        spine.state.clearTracks();
        spine.state.clearListeners();

        // Play collector_particle sound when collect animation plays
        soundManager.play(SOUND_NAME.collector_particle);

        // Play the collection animation (non-looping)
        spine.state.setAnimation(0, animationName, false);

        // Add listener for completion
        spine.state.addListener({
          complete: (entry) => {
            if (entry.animation.name === animationName) {
              // Stop bone update loop for this spine
              if (spineData.boneUpdateTicker) {
                PIXI.Ticker.shared.remove(spineData.boneUpdateTicker);
                this.activeTickers.delete(spineData.boneUpdateTicker);
                spineData.boneUpdateTicker = null;
              }

              // Hide the value text
              if (valueTextContainer) {
                valueTextContainer.alpha = 0;
              }

              // Hide spine after animation completes
              spine.alpha = RENDER_PIPELINE_ALPHA;
              resolve();
            }
          },
        });
      } catch (error) {
        // Cleanup on error
        if (spineData.boneUpdateTicker) {
          PIXI.Ticker.shared.remove(spineData.boneUpdateTicker);
          this.activeTickers.delete(spineData.boneUpdateTicker);
          spineData.boneUpdateTicker = null;
        }
        if (valueTextContainer) {
          valueTextContainer.alpha = 0;
        }
        spine.alpha = RENDER_PIPELINE_ALPHA;
        resolve();
      }
    });
  }

  /**
   * Update text position from bone for a specific spine
   */
  updateTextFromBoneForSpine(spine, boneName, textContainer) {
    if (!spine || !spine.skeleton || !textContainer) return;

    try {
      const bone = spine.skeleton.findBone(boneName);
      if (!bone) return;

      textContainer.x = bone.worldX;
      textContainer.y = bone.worldY;
      textContainer.rotation = 0; // Keep text horizontal

      const scaleX = bone.getWorldScaleX();
      const scaleY = bone.getWorldScaleY();
      textContainer.scale.set(scaleX, scaleY);
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Play end animation (glow effect before collection starts)
   * @returns {Promise} - Resolves when animation completes
   */
  /**
   * Play progress multiplier transfer animation
   * Shows multiplier traveling from progress bar to collector
   * @param {number} multiplierValue - The multiplier value (2 or 5)
   * @returns {Promise} - Resolves when animation completes
   */
  playProgressMultiplierAnimation(multiplierValue) {
    return new Promise((resolve) => {
      if (
        !this.progressMultiplierSpine ||
        !this.progressMultiplierSpine.state
      ) {
        resolve();
        return;
      }

      try {
        // Make spine visible
        this.progressMultiplierSpine.alpha = VISIBLE_ALPHA;

        // Clear any previous animations and listeners
        this.progressMultiplierSpine.state.clearTracks();
        this.progressMultiplierSpine.state.clearListeners();

        // Determine which animation to play based on multiplier value AND orientation
        // Animations are named like "landscape_x2", "landscape_x5", "portrait_x2", "portrait_x5"
        const orientationPrefix =
          this.orientation === "PORTRAIT" ? "portrait" : "landscape";
        const animationName = `${orientationPrefix}_x${multiplierValue}`;

        // Check if animation exists
        const availableAnimations =
          this.progressMultiplierSpine.skeleton?.data?.animations?.map(
            (anim) => anim.name
          ) || [];

        if (!availableAnimations.includes(animationName)) {
          // Try to play first available animation as fallback
          if (availableAnimations.length > 0) {
            this.progressMultiplierSpine.state.setAnimation(
              0,
              availableAnimations[0],
              false
            );
          } else {
            this.progressMultiplierSpine.alpha = RENDER_PIPELINE_ALPHA;
            resolve();
            return;
          }
        } else {
          // Play progress_bar_multiplier sound when playing landscape_x2, landscape_x5, portrait_x2, or portrait_x5 animations
          if (
            animationName === "landscape_x2" ||
            animationName === "landscape_x5" ||
            animationName === "portrait_x2" ||
            animationName === "portrait_x5"
          ) {
            soundManager.play(SOUND_NAME.progress_bar_multiplier);
          }

          // Play the orientation-specific multiplier animation (non-looping)
          this.progressMultiplierSpine.state.setAnimation(
            0,
            animationName,
            false
          );
        }

        // Add listener for completion
        this.progressMultiplierSpine.state.addListener({
          complete: (entry) => {
            // Hide spine after animation completes
            this.progressMultiplierSpine.alpha = RENDER_PIPELINE_ALPHA;
            resolve();
          },
        });
      } catch (error) {
        this.progressMultiplierSpine.alpha = RENDER_PIPELINE_ALPHA;
        resolve();
      }
    });
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    // Mark as cleaned up first to stop any active tickers
    this.isCleanedUp = true;

    // Remove all active tickers from shared ticker
    if (this.activeTickers && this.activeTickers.size > 0) {
      this.activeTickers.forEach(ticker => {
        try {
          PIXI.Ticker.shared.remove(ticker);
        } catch (error) {
          // Silent fail
        }
      });
      this.activeTickers.clear();
    }

    // Cleanup all coin collect spines
    Object.values(this.coinCollectSpines).forEach(spineData => {
      // Stop bone update ticker (redundant but safe)
      if (spineData.boneUpdateTicker) {
        try {
          PIXI.Ticker.shared.remove(spineData.boneUpdateTicker);
        } catch (error) {
          // Silent fail
        }
        spineData.boneUpdateTicker = null;
      }

      // Cleanup spine
      if (spineData.spine) {
        if (spineData.spine.state) {
          try {
            spineData.spine.state.clearListeners();
            spineData.spine.state.clearTracks();
          } catch (error) {
            // Silent fail
          }
        }

        try {
          spineData.spine.destroy();
        } catch (error) {
          // Silent fail
        }
      }
    });
    this.coinCollectSpines = {};

    // Cleanup spine container
    if (this.spineContainer) {
      try {
        this.spineContainer.destroy({ children: true });
      } catch (error) {
        // Silent fail
      }
      this.spineContainer = null;
    }

    // Cleanup progress multiplier spine
    if (this.progressMultiplierSpine) {
      if (this.progressMultiplierSpine.state) {
        try {
          this.progressMultiplierSpine.state.clearListeners();
          this.progressMultiplierSpine.state.clearTracks();
        } catch (error) {
          // Silent fail
        }
      }

      try {
        this.progressMultiplierSpine.destroy();
      } catch (error) {
        // Silent fail
      }

      this.progressMultiplierSpine = null;
    }

    this.activeBoneName = null;
    this.initialized = false;
  }
}

export default HoldAndWinCoinCollectController;
